<template>
  <div v-if="customFieldsExist" :class="containerClasses">
    <div
      :class="[{ 'v-row': isInline }, ...fieldClasses]"
      v-for="field in customFields"
      :key="field.name">
      <h3 v-if="field.label">
        {{ field.label }}:
        <span v-if="field.required" class="text-error">*</span>
        <help-icon-tooltip v-if="field.description">{{ field.description }}</help-icon-tooltip>
      </h3>
      <component
        :data-testid="fieldTestId(field)"
        single-line
        :is="customFormsComponentsMapping[field.type]?.component"
        :field="field"
        :warehouse="warehouse"
        :timezone="warehouse?.timezone"
        :military-time-enabled="$isMilitaryTimeEnabled(warehouse)"
        :display-label="false"
        v-model="field.value"></component>
    </div>
  </div>
</template>

<script>
/**
 * Custom Fields shown in create appointment form
 * @displayName Custom Fields
 */
import {
  CustomFieldType,
  getRequiredValue,
  toKebabCase,
  updateCustomFieldsFromWarehouse
} from '@nova/core';
import { useCustomForms } from '@satellite/composables/useCustomForms';
import { provideCustomFormsStorageContext } from '@satellite/composables/useCustomFormsStorageContext';

const FIELD_TYPES_WITH_OWN_TEST_ID = new Set([
  CustomFieldType.BigString,
  CustomFieldType.DropDownMultiSelect
]);

export default {
  name: 'CustomFields',
  props: {
    /**
     * Warehouse entity providing the custom fields template
     */
    warehouse: Object,
    appointment: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    },
    initialCustomFields: {
      type: Array,
      required: false,
      default: null
    },
    isInline: {
      type: Boolean,
      default: true
    },
    containerClasses: {
      type: Array,
      default() {
        return ['my-4'];
      }
    },
    fieldClasses: {
      type: Array,
      default() {
        return ['mb-4'];
      }
    },
    singleLine: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { customFormsComponentsMapping } = useCustomForms();

    /** A warehouse-configured document field can render here, and injects this to upload. */
    provideCustomFormsStorageContext(() => props.warehouse?.id);

    return { customFormsComponentsMapping };
  },
  computed: {
    customFieldsExist() {
      return this.customFields?.length;
    },
    existingCustomFields() {
      return this.initialCustomFields?.length
        ? this.initialCustomFields
        : this.appointment?.customFields;
    }
  },
  data() {
    return {
      customFields: [],
      areCustomFieldsSet: false
    };
  },
  methods: {
    toKebabCase,
    /**
     * These satellite field types already put the label test id on their inner input. Passing it
     * here too lands a second copy on the wrapper root through attribute fallthrough, and E2E
     * locators by that id then resolve to two elements.
     */
    fieldTestId(field) {
      return FIELD_TYPES_WITH_OWN_TEST_ID.has(field.type) ? undefined : toKebabCase(field.label);
    },
    getRequiredValue(field) {
      return getRequiredValue(field, false);
    },
    setCustomFields() {
      this.customFields = updateCustomFieldsFromWarehouse(
        this.existingCustomFields,
        this.warehouse,
        false
      );
    }
  },
  mounted() {
    this.setCustomFields();
  },
  watch: {
    warehouse() {
      this.setCustomFields();
    },
    customFields() {
      /**
       * Emits input event with new customFields data
       * @event input
       * @property {object} input - customFields data
       */
      this.$emit('update:model-value', this.customFields);
    }
  }
};
</script>
